import React from 'react';

import { TESTING } from '../../captivision.config';
import { CVHeading } from '../../document.component';
import { UI, Jump, Rows } from '../../rail-document.component';
import { Url, Survey, Task, CVLookFor } from '../parts.component';

// The instructions for TestFlight testers, in English. Every other
// testing.<language>.jsx here is the same page translated: keep their
// structure - headings, ids, tasks - in step with this one. <UI> names a
// control the way the app, the App Store or TestFlight labels it in that
// language.

const LookFor = props => <CVLookFor label="Look for" {...props} />

const page = {
    title: 'Testing instructions',
    railLabel: 'Testing contents',
    contents: [
        {
            group: 'Getting the apps',
            links: [
                { id: 'what-you-need', label: 'What you need' },
                { id: 'install-testflight', label: 'Install TestFlight' },
                { id: 'install-host', label: 'Install CaptiVision Host' },
                { id: 'install-camera', label: 'Install CaptiVision Client' },
                { id: 'trouble', label: 'If something goes wrong' },
                { id: 'updates', label: 'Updates' },
                { id: 'feedback', label: 'Sending feedback' }
            ]
        },
        {
            group: 'Test session',
            links: [
                { id: 'before-you-start', label: 'Before you start' },
                { id: 'session', label: 'Setting up a test session' }
            ]
        },
        {
            group: 'Test plans',
            links: [
                { id: 'deaf-tester', label: 'Deaf or hard of hearing tester' },
                { id: 'interpreter', label: 'ASL interpreter' }
            ]
        }
    ],
    head: <>
        <span className="cv-eyebrow"> TestFlight beta </span>
        <h1> Thank you for testing CaptiVision </h1>
        <p className="cv-lede">
            You'll install both apps through TestFlight, Apple's app for testing apps before they are
            released. It's free and safe, and the test apps are removed when you delete them.
        </p>
    </>,
    body: <>
        <p> CaptiVision is two apps that work together: </p>
        <Rows rows={[
            ['CaptiVision Host', 'Runs on an iPad (or a Mac). It shows every camera, highlights whoever is speaking, and writes the captions.'],
            ['CaptiVision Client', 'Runs on each iPhone. Each phone is one camera and microphone in the room.']
        ]} />

        <CVHeading id="what-you-need" num="01"> What you need </CVHeading>
        <Rows rows={[
            ['An iPad', 'With iPadOS 17 or later, for CaptiVision Host. Or a Mac with Apple silicon and macOS 14 or later, if your invitation includes Mac.'],
            ['One or more iPhones', 'With iOS 15.6 or later (iPhone 6s and newer), for CaptiVision Client. Two phones make a better test; a spare iPad can also be a camera.'],
            ['One Wi-Fi network', "That all the devices join. A home or office network works best. Some school, hotel and guest networks stop devices seeing each other; if the phone cannot find the iPad, that's worth telling us."],
            ['A stand for each phone', 'A small tripod, a stand, or a stack of books.'],
            ['A charger', 'For the iPad, if you test for more than half an hour.']
        ]} />
        <p> The apps are free during testing. You will not be asked to pay or to create an account. </p>

        <CVHeading id="install-testflight" num="02"> Install TestFlight </CVHeading>
        <p> On the iPad and on every iPhone: </p>
        <ol>
            <li>Open the App Store.</li>
            <li>Search for "TestFlight" (made by Apple), or open <Url href="https://apps.apple.com/app/testflight/id899247664" /></li>
            <li>Tap <UI>Get</UI>, then <UI>Install</UI>.</li>
        </ol>
        <p className="cv-tip"><b>Older devices</b>If your device is on an older iOS version, the App Store may offer an older version of TestFlight. Accept it.</p>

        <CVHeading id="install-host" num="03"> Install CaptiVision Host </CVHeading>
        <p> On the iPad (or Mac): </p>
        <ol>
            <li>Open this link: <Url href={TESTING.hostInviteUrl} /></li>
            <li>TestFlight opens. Tap <UI>Accept</UI>, then <UI>Install</UI>.</li>
            <li>When it finishes, tap <UI>Open</UI>, or find CaptiVision Host on the Home Screen. An orange dot beside its name shows it's a test version.</li>
        </ol>
        <p className="cv-tip"><b>On a Mac</b>Open the same link on the Mac, install TestFlight from the Mac App Store if asked, then click <UI>Accept</UI> and <UI>Install</UI>.</p>

        <CVHeading id="install-camera" num="04"> Install CaptiVision Client </CVHeading>
        <p> On each iPhone: </p>
        <ol>
            <li>
                Open this link: <Url href={TESTING.cameraInviteUrl} /><br />
                Easiest way: forward this email to yourself and open it on the phone, or send the link to the phone in a message.
            </li>
            <li>TestFlight opens. Tap <UI>Accept</UI>, then <UI>Install</UI>.</li>
            <li>Repeat on every phone you'll use as a camera.</li>
        </ol>
        <p className="cv-tip"><b>Borrowed phones</b>Borrowing a phone from a friend? They install TestFlight and CaptiVision Client the same way, signed in with their own Apple Account.</p>
        <p>
            <strong>Received an email invitation instead of a link?</strong> Open the email on the device and tap
            {' '}<UI>View in TestFlight</UI>. If it gives you a code, open TestFlight, tap <UI>Redeem</UI>, and type the code.
        </p>

        <CVHeading id="trouble" num="05"> If something goes wrong </CVHeading>
        <Rows head={['What you see', 'What to do']} rows={[
            ['"This beta isn\'t accepting any new testers" or "The requested app is not available"', "The test may be full or paused. Reply to me and I'll sort it out."],
            ['The link opens a web page instead of TestFlight', <>Install TestFlight first (<Jump to="install-testflight">step 02</Jump>), then open the link again.</>],
            ['"Requires a newer version of iOS"', "That device can't run this app. Use another device, and let us know which model and iOS version it was."]
        ]} />

        <CVHeading id="updates" num="06"> Updates </CVHeading>
        <p>
            I may send new versions during testing. TestFlight tells you when one is ready. To get them
            automatically: open TestFlight, tap the app, and turn on <UI>Automatic Updates</UI>. Test versions stop
            working after 90 days.
        </p>

        <CVHeading id="feedback" num="07"> Sending feedback while you test </CVHeading>
        <p> The quickest way to report a problem is from the app itself: </p>
        <ul>
            <li>Take a screenshot (on the iPad: top button and volume up together).</li>
            <li>Tap the screenshot preview, then the share button, then <UI>Share Beta Feedback</UI>. Add a sentence about what happened.</li>
            <li>If an app closes unexpectedly, TestFlight may ask whether to send a crash report. Please tap <UI>Share</UI>.</li>
        </ul>
        <p>
            You can also open TestFlight, tap the app, and tap <UI>Send Beta Feedback</UI>. Longer thoughts are best
            in <Survey>the survey</Survey> at the end.
        </p>

        {/* Test session */}
        <span className="cv-part"> Setting up a test session </span>

        <CVHeading id="before-you-start" num="08"> Before you start </CVHeading>
        <ul>
            <li>Tell everyone in the room that the phones will film and listen to them to make captions. Nothing is saved unless you tap <UI>Record</UI>.</li>
            <li>Please don't use this test version for confidential conversations (medical, legal, exams). In this early version the video between the phones and the iPad is not yet encrypted on your Wi-Fi network.</li>
            <li>Note the time when you open the Host app. We'll ask how long setup took and what was difficult.</li>
        </ul>

        <CVHeading id="session" num="09"> Setting up a test session </CVHeading>
        <ol className="cv-walk">
            <Task title="Start a session on the iPad or Mac">
                <p>Open CaptiVision Host. Type a <UI>Session name</UI>, such as "Living Room Test", and tap <UI>Start session</UI>. A short tour points out each button along the top; you can replay it any time with the <UI>?</UI> button.</p>
                <p>Allow the Local Network and Microphone prompts. The iPad needs local network access to find the phones.</p>
            </Task>

            <Task title="Get captions ready on the iPad or Mac">
                <p>If the top of the screen says captions are off, tap <UI>Set up</UI>.</p>
                <ul>
                    <li>On iPadOS 26 or later, Apple's built-in speech is used. The iPad may download your language once.</li>
                    <li>On earlier versions, download Whisper small (under 200 MB). Use Wi-Fi.</li>
                </ul>
                <p>Tap <UI>Check captioning works</UI>. The iPad plays an 11-second sample and shows the captions it wrote.</p>
            </Task>

            <Task title="Add a phone">
                <p>On the iPad (or Mac), tap <UI>Add camera</UI>. A code appears. Then on the iPhone:</p>
                <ul>
                    <li>Open CaptiVision Client and tap <UI>Scan the host's code</UI>. Point the phone at the code on the iPad (or Mac).</li>
                    <li>Or pick your session from the list under <UI>On this network</UI>.</li>
                    <li>Or, on the iPad (or Mac), tap <UI>Copy pairing link</UI>, send it to the phone (for example in a message), copy it there, and tap <UI>Paste a pairing link</UI>.</li>
                </ul>
                <p>Allow Camera, Microphone and Local Network on the phone. If you tapped <UI>Don't Allow</UI> by mistake, open the iPhone's Settings › Privacy &amp; Security › Local Network (or Camera, or Microphone) and turn CaptiVision Client on.</p>
                <p>The phone shows what it will share. Check the camera's name, choose the <UI>Role</UI> (Camera for people who talk, Interpreter for a sign language interpreter), and tap <UI>Start streaming</UI>. The phone's picture appears on the iPad within a few seconds.</p>
                <p className="cv-tip"><b>Tip</b>Codes last ten minutes. If the phone says the code has expired, tap <UI>New code</UI> on the iPad  (or Mac).</p>
            </Task>

            <Task title="Turn on Do Not Disturb on each phone">
                <p>A phone call takes over the camera. When the phone asks, follow the steps it shows (Focus, then Do Not Disturb). The prompt closes by itself once it's on. If Do Not Disturb is already on but the phone still asks, tap <UI>It's already on</UI>.</p>
            </Task>

            <Task title="Place the phones">
                <ul>
                    <li>Point each phone at the people who will talk, so their faces are clearly visible, a little below eye level.</li>
                    <li>Keep phones within a few metres (about 10 feet) of the people they film. The closer the phone, the better the captions.</li>
                    <li>Use the level line on the phone's screen. It disappears when the phone is straight.</li>
                    <li>After a few minutes a phone may darken its screen to stay cool. It's still filming. Tap the screen to wake it.</li>
                </ul>
            </Task>

            <Task title="You're live">
                <p>When someone talks, an amber outline appears around them and captions run along the bottom with their name. The view buttons at the top choose what you see:</p>
                <Rows rows={[
                    ['Grid', 'Every camera at once.'],
                    ['Auto', 'Whoever is speaking, full screen.'],
                    ['Two-Feed', 'One camera (often the interpreter) beside the speaker.']
                ]} />
                <p>To name someone, tap the badge next to their caption, or open <UI>Speakers</UI> (the person button at the top). Their earlier captions update too.</p>
            </Task>

            <Task title="Finish">
                <p>Tap <UI>Leave</UI> on the iPad (or Mac) and confirm. Every phone disconnects and keeps nothing from the session.</p>
            </Task>
        </ol>

        {/* Test plans */}
        <span className="cv-part"> Test plans </span>

        <CVHeading id="deaf-tester" num="10"> Deaf or hard of hearing tester </CVHeading>
        <p className="cv-plan-question"> Can you follow a conversation from the iPad? </p>
        <Rows rows={[
            ['Time', 'About 30 minutes.'],
            ['People', 'You, plus one or two people who speak (friends, family, colleagues). Three voices is ideal.'],
            ['Devices', 'The iPad with CaptiVision Host, and one or two iPhones with CaptiVision Client.'],
            ['Setting', "Sit where you'd normally sit in a class or meeting, with the iPad in front of you. The speakers sit or stand facing the phones."]
        ]} />
        <p>
            You don't need to do every task. Write a few words after each one: what worked, what didn't, and
            anything that surprised you. Rough notes are perfect.
        </p>

        <ol className="cv-walk">
            <Task title="Set it up" time="10–15 minutes">
                <p>Follow <Jump to="session">Setting up a test session</Jump>. If someone helps you, that's fine; note what they helped with.</p>
                <LookFor><p>How long it took from opening the Host app to seeing the first phone's picture. Which step was hardest?</p></LookFor>
            </Task>

            <Task title="One person reads" time="3 minutes">
                <p>Ask one speaker to read this aloud at a normal pace, facing the phone:</p>
                <blockquote className="cv-script">
                    "Good morning, everyone. Today is Tuesday, and we have three things to cover. First, the budget
                    for next year is forty-two thousand dollars. Second, Amara and Kwame will present the survey
                    results from Addis Ababa and Banjul. Third, please hand in your reports by Friday the
                    twenty-fourth. Any questions before we start?"
                </blockquote>
                <LookFor>
                    <ul>
                        <li>Are the captions correct? Note words that were wrong, especially names and numbers.</li>
                        <li>How long after the person speaks do the captions appear?</li>
                        <li>Is the amber outline around the right person?</li>
                        <li>Can you read the captions comfortably from where you sit?</li>
                    </ul>
                </LookFor>
            </Task>

            <Task title="Name the speakers" time="2 minutes">
                <p>Tap the badge next to a caption, or open <UI>Speakers</UI>, and give each speaker their name.</p>
                <LookFor><p>Did the earlier captions change to the new name? Was it easy to find where to name someone?</p></LookFor>
            </Task>

            <Task title="Taking turns" time="5 minutes">
                <p>Ask two or three people to have a normal conversation, for example planning a weekend trip. Every person should speak several times.</p>
                <LookFor>
                    <ul>
                        <li>When the speaker changes, how quickly does the outline move to them?</li>
                        <li>Are captions credited to the right person?</li>
                        <li>Does the magnified close-up of the speaker help or distract you?</li>
                    </ul>
                </LookFor>
            </Task>

            <Task title="Harder moments" time="5 minutes">
                <p>Try each of these for about 30 seconds:</p>
                <ol className="cv-alpha">
                    <li>Two people talk at the same time, or interrupt each other.</li>
                    <li>Someone speaks while turned away from the phone, or covering their mouth.</li>
                    <li>Someone speaks quietly from the far side of the room.</li>
                    <li>Music or a TV plays in the background.</li>
                    <li>Someone sits with a bright window behind them.</li>
                </ol>
                <LookFor><p>What went wrong, and could you still follow along?</p></LookFor>
            </Task>

            <Task title="Try each view" time="5 minutes">
                <p>During a conversation, switch between <UI>Grid</UI>, <UI>Auto</UI> and <UI>Two-Feed</UI>.</p>
                <LookFor><p>Which view is easiest to follow? Which would you use in a real class or meeting, and why?</p></LookFor>
            </Task>

            <Task title="Caption size and lines" time="3 minutes">
                <p>Tap the <UI>Captions</UI> button to change the text size and how many lines stay on screen. Try pinching the captions to resize them.</p>
                <LookFor><p>What size and number of lines worked best at your distance?</p></LookFor>
            </Task>

            <Task title="Accessibility" time="optional, 3 minutes">
                <p>If you use VoiceOver, larger text, or other accessibility settings, keep them on during the test.</p>
                <LookFor><p>Did the app ever rely on a sound, a voice prompt, or an alert you couldn't see? Was anything hard to read?</p></LookFor>
            </Task>

            <Task title="Record" time="optional, 3 minutes">
                <p>Tap <UI>Record</UI>, choose a folder, talk for a minute, then stop recording.</p>
                <LookFor><p>Was it clear where the recording went? Open the folder: are the video, captions and transcript what you expected?</p></LookFor>
            </Task>

            <Task title="Finish">
                <p>Tap <UI>Leave</UI>. Check that each phone says the session has ended. Then fill in <Survey>the survey</Survey> while it's fresh.</p>
            </Task>
        </ol>

        <CVHeading id="interpreter" num="11"> ASL interpreter </CVHeading>
        <p className="cv-plan-question"> Does the interpreter view work, for the Deaf viewer and for you? </p>
        <p>
            Works for any sign language; replace "ASL" as needed. Best run with a Deaf tester watching the iPad at
            the same time (see the <Jump to="deaf-tester">Deaf or hard of hearing tester</Jump> plan), but an
            interpreter can do it alone by watching the iPad during breaks and watching a recording afterwards.
        </p>
        <Rows rows={[
            ['Time', 'About 30 minutes.'],
            ['People', 'You, at least one hearing speaker, and ideally a Deaf viewer at the iPad.'],
            ['Devices', <>The iPad with CaptiVision Host, and two iPhones:<br /><strong>Phone 1</strong> points at the speakers, role Camera.<br /><strong>Phone 2</strong> points at you, role Interpreter.</>]
        ]} />

        <ol className="cv-walk">
            <Task title="Set up the interpreter camera" time="10 minutes">
                <p>Follow <Jump to="session">Setting up a test session</Jump>. When Phone 2 joins, choose <UI>Interpreter</UI> as its <UI>Role</UI> before tapping <UI>Start streaming</UI>. Already joined as Camera? On the iPad (or Mac), touch and hold its picture, then choose <UI>Role</UI> › <UI>Interpreter</UI>.</p>
                <p>Place Phone 2 so your whole signing space is in view: top of the head to the waist, and both elbows fully extended. Try the phone upright and on its side.</p>
                <LookFor>
                    <ul>
                        <li>Was it clear how to mark this phone as the interpreter?</li>
                        <li>Upright or sideways: which framed your signing space better on the iPad?</li>
                        <li>How far away did the phone need to be? Did lighting or your clothing and background affect how clearly your hands showed?</li>
                    </ul>
                </LookFor>
            </Task>

            <Task title="Keep the interpreter on screen" time="5 minutes">
                <p>On the iPad (or Mac), choose <UI>Two-Feed</UI>, and tap your picture to pin it. Ask two people to take turns speaking while you interpret.</p>
                <LookFor>
                    <ul>
                        <li>Do you stay on screen while the speaker view changes?</li>
                        <li>Is the interpreter picture large enough for a Deaf viewer to read comfortably?</li>
                        <li>Where on the screen would you want the interpreter to be?</li>
                    </ul>
                </LookFor>
            </Task>

            <Task title="Clarity and delay" time="5 minutes">
                <p>Interpret normally, then include:</p>
                <ol className="cv-alpha">
                    <li>Fast fingerspelling: names like "Kwame Mensah" or "Tewodros".</li>
                    <li>Numbers, dates and a few fast classifier descriptions.</li>
                    <li>Facial grammar and mouth movements.</li>
                </ol>
                <p>Ask the Deaf viewer (or watch a recording) whether each was readable. Each camera's picture on the iPad (or Mac) shows how many milliseconds it runs behind.</p>
                <LookFor>
                    <ul>
                        <li>Was fingerspelling clear, or blurred or jerky?</li>
                        <li>Was the delay noticeable? Did your signing arrive in step with the captions and the speaker's picture?</li>
                    </ul>
                </LookFor>
            </Task>

            <Task title="Signing without voicing" time="3 minutes">
                <p>Interpret silently for a minute while no one near your phone speaks. The app looks for mouths moving together with sound.</p>
                <LookFor><p>Does the iPad (or Mac) ever outline you as the speaker, or add captions under your name, while you sign without speaking?</p></LookFor>
            </Task>

            <Task title="Voicing for a Deaf signer" time="5 minutes">
                <p>If a Deaf person is present, have them sign a few comments while you voice.</p>
                <LookFor>
                    <ul>
                        <li>Whose name do the captions show: yours, or the Deaf signer's?</li>
                        <li>Which would you want? For example "[Amara]:", "[Interpreter]:" or "[Amara, via interpreter]:"</li>
                        <li>Did your voice come through clearly from your phone's microphone?</li>
                    </ul>
                </LookFor>
                <p>Try muting your phone's microphone (the microphone button on Phone 2) and voicing again. Does that help or hurt the captions?</p>
            </Task>

            <Task title="Switching interpreters" time="optional, 3 minutes">
                <p>If you work in a team, swap seats as you would at a hand-off.</p>
                <LookFor><p>Did the phone need moving? Did the switch cause any problem on the iPad (or Mac)?</p></LookFor>
            </Task>

            <Task title="Your own view of the room" time="3 minutes">
                <p>Look at the iPad (or Phone 1's picture on it) while interpreting.</p>
                <LookFor><p>Would seeing the highlighted speaker and captions help you follow who is talking in a large room? Where would you put the iPad (or Mac)?</p></LookFor>
            </Task>

            <Task title="Recording indicator" time="2 minutes">
                <p>Ask the person at the iPad to tap <UI>Record</UI>, then stop.</p>
                <LookFor><p>Did your phone clearly show that the host was recording? Is that enough notice for an interpreter being filmed?</p></LookFor>
            </Task>

            <Task title="Finish">
                <p>Tap <UI>Leave</UI> on the iPad (or Mac). Then fill in <Survey>the survey</Survey>, including the interpreter questions.</p>
            </Task>
        </ol>
    </>
}

export default page;
